// SPDX-License-Identifier: AGPL-3.0-or-later

// LiveKit participant attribute the API writes with the worn room character; clients only read it.
export const ROOM_CHARACTER_ATTRIBUTE = 'micup.character';
// LiveKit data topics inside a voice room: emotes reliable, head direction and walking position lossy; a newcomer's
// request for the walkers' positions reliable, with an empty body.
export const ROOM_EMOTE_TOPIC = 'micup.room.emote.v1';
export const ROOM_HEAD_TOPIC = 'micup.room.head.v1';
export const ROOM_POS_TOPIC = 'micup.room.pos.v1';
export const ROOM_POS_REQUEST_TOPIC = 'micup.room.pos.req.v1';

// Shirt colours the default outfit can wear; stored as 0xRRGGBB so reordering never changes a saved choice.
export const ROOM_SHIRT_PALETTE: ReadonlyArray<number> = [
	0xd9a441, 0xe0796b, 0x5f8577, 0x50607a, 0x6e5a8e, 0xc9b28c, 0xf0e6d2, 0x3d4249, 0xd96b6b, 0x6da256, 0x4a90c2,
	0xe6a1c0, 0x8a4636, 0x2f6f6a, 0xf2c14e, 0x9b7bd4,
];

// The shirt of someone who never picked one: FNV-1a (32-bit) of the decimal user ID, mod the palette size.
export function defaultShirtColor(userId: string): number {
	let hash = 0x811c9dc5;
	for (let i = 0; i < userId.length; i++) {
		hash = Math.imul(hash ^ userId.charCodeAt(i), 0x01000193) >>> 0;
	}
	return ROOM_SHIRT_PALETTE[hash % ROOM_SHIRT_PALETTE.length]!;
}

const CHARACTER_ATTRIBUTE = /^([a-z0-9-]{1,32});([0-9a-f]{6})$/;

// '<model_key>;<rrggbb>', e.g. 'ithp-gunluk;d9a441'.
export function formatCharacterAttribute(key: string, shirt: number): string {
	return `${key};${shirt.toString(16).padStart(6, '0')}`;
}

export function parseCharacterAttribute(value: string | undefined): {key: string; shirt: number} | null {
	const match = value === undefined ? null : CHARACTER_ATTRIBUTE.exec(value);
	return match ? {key: match[1]!, shirt: Number.parseInt(match[2]!, 16)} : null;
}

export const ROOM_EMOTE_BAR: ReadonlyArray<string> = ['😂', '😢', '😮', '😡', '❤️', '👍', '🎉', '🔥'];

export type RoomExpression = 'laugh' | 'sad' | 'surprised' | 'angry' | 'love';

// Emojis that also change the character's face; the rest only float above it.
export const ROOM_EMOTE_EXPRESSIONS: Readonly<Record<string, RoomExpression>> = {
	'😂': 'laugh',
	'😢': 'sad',
	'😮': 'surprised',
	'😡': 'angry',
	'❤️': 'love',
};

// The SFU forwards walkers × (people − 1) × POS_SEND_HZ position packets a second per room: 9,920 when all 32
// walk. If a 48-person rehearsal shows the data channel above 15% of the SFU's CPU, set POS_SEND_HZ to 5 and
// posMaxParticipants to 24; the receive limit follows the send rate.
const POS_SEND_HZ = 10;

export const ROOM_LIMITS = {
	emotePerSecond: 1,
	emotePerTenSeconds: 5,
	emotePayloadMaxBytes: 96,
	headSendHz: 2,
	headReceiveMaxHz: 4,
	headMinDeltaDeg: 5,
	headMaxParticipants: 25,
	headYawMaxDeg: 35,
	headPitchMaxDeg: 15,
	bubblesMax: 12,
	bubbleQueueMax: 24,
	bubbleTextMax: 120,
	charactersMax: 48,
	standingCharactersMax: 24,
	characterFps: 15,
	characterLodM: 10,
	posSendHz: POS_SEND_HZ,
	posReceiveMaxHz: POS_SEND_HZ + POS_SEND_HZ / 5,
	// Walking is off while more people than this are in the room.
	posMaxParticipants: 32,
	posMaxSpeedMps: 6,
	// A walker standing still repeats its position this often, for as long as it stands.
	posIdleHeartbeatS: 2,
} as const;

const HEAD_VERSION = 1;

function clampDegrees(value: number, max: number): number {
	return Math.max(-max, Math.min(max, Math.round(value) || 0));
}

// [version 1, yaw int8, pitch int8], whole degrees.
export function encodeHead(yawDeg: number, pitchDeg: number): Uint8Array<ArrayBuffer> {
	return Uint8Array.of(
		HEAD_VERSION,
		clampDegrees(yawDeg, ROOM_LIMITS.headYawMaxDeg) & 0xff,
		clampDegrees(pitchDeg, ROOM_LIMITS.headPitchMaxDeg) & 0xff,
	);
}

export function decodeHead(bytes: Uint8Array): {yawDeg: number; pitchDeg: number} | null {
	if (bytes.length !== 3 || bytes[0] !== HEAD_VERSION) return null;
	return {
		yawDeg: clampDegrees((bytes[1]! << 24) >> 24, ROOM_LIMITS.headYawMaxDeg),
		pitchDeg: clampDegrees((bytes[2]! << 24) >> 24, ROOM_LIMITS.headPitchMaxDeg),
	};
}

// Wire order of the walking animations; an unknown value reads as idle.
export const ROOM_POSE_ANIMS = ['idle', 'walk', 'run', 'sit'] as const;
export type RoomPoseAnim = (typeof ROOM_POSE_ANIMS)[number];

// A walking character: its feet in room metres, the way it faces in radians about +Y.
export interface RoomPose {
	x: number;
	y: number;
	z: number;
	yaw: number;
	anim: RoomPoseAnim;
}

const POS_VERSION = 1;
const POS_BYTES = 10;
const POS_FLAG_END = 1;
const TURN = 2 * Math.PI;

function centimetres(metres: number): number {
	const value = Math.round(metres * 100);
	return Number.isFinite(value) ? Math.max(-0x8000, Math.min(0x7fff, value)) : 0;
}

// [version 1, x int16, y int16, z int16 (cm, big-endian), yaw uint8 (a turn in 256 steps), anim uint8, flags uint8].
// Flag bit 0, end: the walker is back in its seat. The other flag bits are ignored.
export function encodePos(pose: RoomPose, end = false): Uint8Array<ArrayBuffer> {
	const packet = new Uint8Array(POS_BYTES);
	const view = new DataView(packet.buffer);
	packet[0] = POS_VERSION;
	view.setInt16(1, centimetres(pose.x));
	view.setInt16(3, centimetres(pose.y));
	view.setInt16(5, centimetres(pose.z));
	packet[7] = (Math.round((pose.yaw / TURN) * 256) || 0) & 0xff;
	packet[8] = ROOM_POSE_ANIMS.indexOf(pose.anim);
	packet[9] = end ? POS_FLAG_END : 0;
	return packet;
}

export function decodePos(bytes: Uint8Array): (RoomPose & {end: boolean}) | null {
	if (bytes.length !== POS_BYTES || bytes[0] !== POS_VERSION) return null;
	const view = new DataView(bytes.buffer, bytes.byteOffset, POS_BYTES);
	return {
		x: view.getInt16(1) / 100,
		y: view.getInt16(3) / 100,
		z: view.getInt16(5) / 100,
		yaw: (bytes[7]! / 256) * TURN,
		anim: ROOM_POSE_ANIMS[bytes[8]!] ?? 'idle',
		end: (bytes[9]! & POS_FLAG_END) !== 0,
	};
}
